// SiteNoticeBanner.jsx — admin-controlled top banner shown across the site,
// agent and affiliate panels. Polls `/api/public/site-notice` every 10s so
// admin can push a maintenance / info / warning / promo announcement that
// appears live without requiring users to refresh.
//
// Behaviour notes:
//   • 4 severity types — each gets its own colour story + iconography
//   • Optional Bangladesh-time schedule with live countdown
//   • Auto-hide once `end_at` has passed (instant, doesn't wait for next poll)
//   • Dismissal is session-only: the X button hides the banner for the rest
//     of the current page session, but a page reload (or re-entering the
//     site) re-shows it — the admin explicitly asked for this so important
//     downtime notices are never silenced permanently.
//   • Responsive: a single horizontal strip on desktop, gracefully stacked
//     into a compact vertical card on phones.

import { useEffect, useState } from "react";
import { X, Wrench, AlertTriangle, Info, Megaphone } from "lucide-react";
import { safeFetch } from "../../lib/safeFetch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Per-type design system — gradients are kept short-range so the banner
// reads as a single colour story even on ultra-wide screens.
const TYPE_STYLES = {
  maintenance: {
    bg: "linear-gradient(95deg,#3b0a0a 0%,#7f1d1d 35%,#b91c1c 65%,#7f1d1d 100%)",
    overlay: "radial-gradient(60% 120% at 0% 50%, rgba(255,255,255,0.10), transparent 60%)",
    accent: "#fecaca",
    accentBright: "#fee2e2",
    badgeBg: "rgba(15,5,5,0.55)",
    ink: "#fef2f2",
    titleInk: "#ffffff",
    border: "#fca5a5",
    glow: "0 8px 24px -8px rgba(185,28,28,0.55)",
    Icon: Wrench,
    label: "Scheduled Maintenance",
  },
  warning: {
    bg: "linear-gradient(95deg,#3f1e07 0%,#78350f 35%,#b45309 65%,#78350f 100%)",
    overlay: "radial-gradient(60% 120% at 0% 50%, rgba(255,255,255,0.10), transparent 60%)",
    accent: "#fde68a",
    accentBright: "#fef3c7",
    badgeBg: "rgba(10,6,2,0.55)",
    ink: "#fffbeb",
    titleInk: "#ffffff",
    border: "#fbbf24",
    glow: "0 8px 24px -8px rgba(180,83,9,0.55)",
    Icon: AlertTriangle,
    label: "Important Notice",
  },
  info: {
    bg: "linear-gradient(95deg,#020617 0%,#0c2540 35%,#0369a1 65%,#0c2540 100%)",
    overlay: "radial-gradient(60% 120% at 0% 50%, rgba(255,255,255,0.10), transparent 60%)",
    accent: "#7dd3fc",
    accentBright: "#bae6fd",
    badgeBg: "rgba(2,6,23,0.55)",
    ink: "#e0f2fe",
    titleInk: "#ffffff",
    border: "#38bdf8",
    glow: "0 8px 24px -8px rgba(3,105,161,0.55)",
    Icon: Info,
    label: "Latest Update",
  },
  promo: {
    bg: "linear-gradient(95deg,#022c1e 0%,#064e3b 35%,#047857 65%,#064e3b 100%)",
    overlay: "radial-gradient(60% 120% at 0% 50%, rgba(255,255,255,0.10), transparent 60%)",
    accent: "#6ee7b7",
    accentBright: "#d1fae5",
    badgeBg: "rgba(2,15,11,0.55)",
    ink: "#ecfdf5",
    titleInk: "#ffffff",
    border: "#34d399",
    glow: "0 8px 24px -8px rgba(4,120,87,0.55)",
    Icon: Megaphone,
    label: "Announcement",
  },
};

function _fmtCountdown(ms) {
  if (ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (d > 0) return `${d}d ${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

// One-shot inline stylesheet (animations + responsive overrides). We
// inject it once on mount so the component is fully self-contained and
// doesn't depend on an external CSS file or design-system primitive.
const CSS = `
@keyframes ml-notice-pulse {
  0%, 100% { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
  50%      { box-shadow: 0 0 0 6px transparent; opacity: 0.6; }
}
@keyframes ml-notice-sweep {
  0%   { transform: translateX(-120%); }
  100% { transform: translateX(120%); }
}
.ml-notice-pulse-dot { animation: ml-notice-pulse 2s infinite ease-out; }
.ml-notice-sweep {
  position: absolute; inset: 0; pointer-events: none; overflow: hidden;
}
.ml-notice-sweep::after {
  content: ""; position: absolute; top: 0; bottom: 0; width: 22%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.10), transparent);
  filter: blur(2px);
  animation: ml-notice-sweep 7s infinite cubic-bezier(.6,0,.4,1);
}
.ml-notice-close {
  background: rgba(0,0,0,0.30) !important;
  border: 1px solid rgba(255,255,255,0.18) !important;
  transition: background .15s, transform .15s, border-color .15s;
}
.ml-notice-close:hover {
  background: rgba(0,0,0,0.50) !important;
  border-color: rgba(255,255,255,0.40) !important;
  transform: rotate(90deg);
}
@media (max-width: 640px) {
  .ml-notice-bar {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 6px !important;
    padding: 8px 40px 9px 12px !important;
  }
  .ml-notice-head { width: 100%; display: flex !important; align-items: center; gap: 8px; }
  .ml-notice-pill {
    font-size: 9px !important;
    letter-spacing: 0.9px !important;
    padding: 3px 8px 3px 7px !important;
    gap: 6px !important;
  }
  .ml-notice-pill-icon { display: none !important; }
  .ml-notice-text {
    width: 100%;
    font-size: 12px !important;
    line-height: 1.45 !important;
  }
  .ml-notice-title {
    font-size: 12.5px !important;
    margin-right: 4px !important;
  }
  .ml-notice-title-sep { display: none !important; }
  .ml-notice-countdown {
    align-self: flex-start !important;
    font-size: 10.5px !important;
    padding: 3px 8px !important;
    gap: 5px !important;
  }
  .ml-notice-countdown-label { font-size: 8.5px !important; letter-spacing: 1px !important; }
  .ml-notice-close-wrap {
    position: absolute !important;
    top: 6px !important;
    right: 6px !important;
    z-index: 2;
  }
  .ml-notice-close-mobile {
    width: 26px !important;
    height: 26px !important;
    border-radius: 7px !important;
  }
  .ml-notice-close-desktop { display: none !important; }
}
@media (min-width: 641px) {
  .ml-notice-close-wrap { display: none !important; }
}`;

function useInjectStyles() {
  useEffect(() => {
    const id = "ml-notice-styles";
    if (document.getElementById(id)) return undefined;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = CSS;
    document.head.appendChild(el);
    return undefined;
  }, []);
}

export default function SiteNoticeBanner() {
  useInjectStyles();
  const [notice, setNotice] = useState(null); // server response (or null)
  // Per-session, in-memory dismissal — wiped on every full page reload, so a
  // user that closed the banner once still sees it the next time they enter
  // the site / agent / affiliate panel. This is intentional: maintenance
  // notices should never be silenced permanently by a single click.
  const [dismissedVersion, setDismissedVersion] = useState(0);
  // Tracks "now" for the countdown / auto-hide logic. Initialised lazily so
  // SSR (if ever added) doesn't drift between server and client renders.
  const [now, setNow] = useState(() => Date.now());

  // Poll the public endpoint every 10s. We use `fetch` (instead of axios)
  // because the banner is shown on PUBLIC pages too — unauthenticated, no
  // credentials, no cookies, no interceptors.
  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const r = await safeFetch(`${BACKEND_URL}/api/public/site-notice`, {
          cache: "no-store",
          credentials: "omit",
          timeoutMs: 8000,
        });
        if (!r.ok) return;
        const data = await r.json();
        if (alive) setNotice(data);
      } catch {
        /* swallow — banner is non-critical */
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 10000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // 1s tick — drives the countdown text and the auto-hide cutoff.
  useEffect(() => {
    if (!notice?.visible) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [notice?.visible]);

  if (!notice || !notice.visible) return null;
  if (notice.dismissible && dismissedVersion === notice.version) return null;

  // Auto-hide once `end_at` has passed (don't wait for next 10s poll).
  if (notice.end_at) {
    const endTs = Date.parse(notice.end_at);
    if (!Number.isNaN(endTs) && now > endTs) return null;
  }

  // Optional countdown logic — "Starts in X" while pre-window, "Ends in X" while live.
  let countdownLabel = null;
  let countdownPhase = null;
  if (notice.show_countdown) {
    if (notice.start_at) {
      const startTs = Date.parse(notice.start_at);
      if (!Number.isNaN(startTs) && now < startTs) {
        const fmt = _fmtCountdown(startTs - now);
        if (fmt) {
          countdownLabel = fmt;
          countdownPhase = "Starts in";
        }
      }
    }
    if (!countdownLabel && notice.end_at) {
      const endTs = Date.parse(notice.end_at);
      if (!Number.isNaN(endTs) && now < endTs) {
        const fmt = _fmtCountdown(endTs - now);
        if (fmt) {
          countdownLabel = fmt;
          countdownPhase = "Ends in";
        }
      }
    }
  }

  const s = TYPE_STYLES[notice.type] || TYPE_STYLES.maintenance;
  const IconCmp = s.Icon;

  const handleDismiss = () => setDismissedVersion(notice.version);

  return (
    <div
      data-testid="site-notice-banner"
      role="status"
      aria-live="polite"
      className="ml-notice-root"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        background: s.bg,
        color: s.ink,
        borderBottom: `1px solid ${s.border}`,
        boxShadow: s.glow,
        fontFamily:
          "'Hind Siliguri','Noto Sans Bengali',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif",
        overflow: "hidden",
        zIndex: 2147483640,
      }}
    >
      {/* Subtle radial overlay + animated sweep give the banner a premium feel */}
      <div
        aria-hidden
        style={{ position: "absolute", inset: 0, background: s.overlay, pointerEvents: "none" }}
      />
      <div aria-hidden className="ml-notice-sweep" />

      <div
        className="ml-notice-bar"
        style={{
          position: "relative",
          maxWidth: 1400,
          margin: "0 auto",
          padding: "11px 18px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "nowrap",
        }}
      >
        {/* Head row — type chip + (on mobile) close button */}
        <div className="ml-notice-head">
          <span
            className="ml-notice-pill"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "5px 11px 5px 9px",
              borderRadius: 999,
              background: s.badgeBg,
              border: `1px solid ${s.border}`,
              fontSize: 10.5,
              letterSpacing: "1.4px",
              textTransform: "uppercase",
              fontWeight: 700,
              color: s.accentBright,
              flexShrink: 0,
              boxShadow: "0 1px 0 rgba(255,255,255,0.06) inset",
              whiteSpace: "nowrap",
            }}
          >
            <span
              aria-hidden
              className="ml-notice-pulse-dot"
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: s.accent,
                color: s.accent,
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <IconCmp className="ml-notice-pill-icon" size={12.5} strokeWidth={2.4} />
            {s.label}
          </span>
          {/* Mobile-only close button (hidden on >=641px via CSS) */}
          {notice.dismissible ? (
            <div className="ml-notice-close-wrap">
              <button
                type="button"
                onClick={handleDismiss}
                aria-label="Dismiss notice"
                className="ml-notice-close ml-notice-close-mobile"
                style={{
                  color: s.ink,
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <X size={15} strokeWidth={2.4} />
              </button>
            </div>
          ) : null}
        </div>

        {/* Main message block */}
        <div
          className="ml-notice-text"
          style={{ flex: 1, minWidth: 0, lineHeight: 1.55, fontSize: 13.5 }}
        >
          {notice.title ? (
            <span
              className="ml-notice-title"
              style={{
                fontWeight: 700,
                fontSize: 14,
                marginRight: 8,
                color: s.titleInk,
                letterSpacing: "0.1px",
              }}
            >
              {notice.title}
              <span className="ml-notice-title-sep" style={{ color: s.accent, margin: "0 6px", opacity: 0.6 }}>·</span>
            </span>
          ) : null}
          <span
            style={{
              color: s.ink,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {notice.message}
          </span>
        </div>

        {/* Countdown badge — monospace clock + soft glow */}
        {countdownLabel ? (
          <span
            data-testid="site-notice-countdown"
            className="ml-notice-countdown"
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              background: s.badgeBg,
              border: `1px solid ${s.border}`,
              fontSize: 12,
              fontFamily:
                "'SF Mono','SFMono-Regular',Consolas,'Liberation Mono',Menlo,monospace",
              color: s.accentBright,
              fontWeight: 700,
              letterSpacing: "0.6px",
              flexShrink: 0,
              boxShadow: "0 1px 0 rgba(255,255,255,0.06) inset",
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              className="ml-notice-countdown-label"
              style={{
                fontSize: 9.5,
                letterSpacing: "1.4px",
                textTransform: "uppercase",
                opacity: 0.85,
                fontWeight: 700,
                fontFamily:
                  "-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif",
              }}
            >
              {countdownPhase}
            </span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{countdownLabel}</span>
          </span>
        ) : null}

        {/* Desktop close button (hidden on mobile via CSS rule) */}
        {notice.dismissible ? (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss notice"
            data-testid="site-notice-dismiss"
            className="ml-notice-close ml-notice-close-desktop"
            style={{
              color: s.ink,
              width: 30,
              height: 30,
              borderRadius: 8,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <X size={15} strokeWidth={2.4} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
